import { useMemo, useEffect } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  BackgroundVariant,
  type Node,
  type Edge,
  useNodesState,
  useEdgesState,
} from '@xyflow/react';
import dagre from 'dagre';
import '@xyflow/react/dist/style.css';

import type { PviGraphWithSpans } from '../../editor/pviTypes';
import { getGraphWarnings, type GraphWarning } from '../../editor/pviGraphService';
import { PviFlowNode, type PviNodeData } from './PviFlowNode';
import { usePviStore } from '../../stores/usePviStore';
import { useSimulationStore } from '../../stores/useSimulationStore';
import { useAdversarialStore } from '../../stores/useAdversarialStore';
import { useDiagnosticStore } from '../../stores/useDiagnosticStore';
import type { SimNodeStatus } from '../../editor/simulationEngine';

interface GraphRendererProps {
  graph: PviGraphWithSpans;
  stale?: boolean;
}

const NODE_WIDTH = 180;
const NODE_HEIGHT = 100;

const EDGE_COLORS: Record<string, string> = {
  HOLD: '#3b82f6',
  PAY: '#22c55e',
  RELEASE: '#14b8a6',
  REFUND: '#f97316',
  SPLIT: '#a855f7',
  MINT: '#eab308',
  BURN: '#ef4444',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const nodeTypes: Record<string, any> = { pviNode: PviFlowNode };

function layoutGraph(graph: PviGraphWithSpans): { nodes: Node[]; edges: Edge[] } {
  const highlightedNodeId = usePviStore.getState().highlightedNodeId;
  const warnings = getGraphWarnings(graph);
  const warningsByNode = new Map<number, GraphWarning[]>();
  for (const w of warnings) {
    const arr = warningsByNode.get(w.nodeId) ?? [];
    arr.push(w);
    warningsByNode.set(w.nodeId, arr);
  }

  // Dagre layout
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'TB', nodesep: 40, ranksep: 60, marginx: 20, marginy: 20 });
  g.setDefaultEdgeLabel(() => ({}));

  for (const n of graph.nodes) {
    g.setNode(String(n.id), { width: NODE_WIDTH, height: NODE_HEIGHT });
  }
  for (const e of graph.edges) {
    g.setEdge(String(e.source), String(e.destination));
  }

  dagre.layout(g);

  // Find the max Y position to add bottom padding
  let maxY = 0;
  for (const n of graph.nodes) {
    const pos = g.node(String(n.id));
    if (pos) maxY = Math.max(maxY, pos.y + NODE_HEIGHT / 2);
  }

  const rfNodes: Node[] = graph.nodes.map(n => {
    const pos = g.node(String(n.id));
    return {
      id: String(n.id),
      type: 'pviNode',
      position: { x: (pos?.x ?? 0) - NODE_WIDTH / 2, y: (pos?.y ?? 0) - NODE_HEIGHT / 2 },
      data: {
        pviNode: n,
        warnings: warningsByNode.get(n.id) ?? [],
        highlighted: n.id === highlightedNodeId,
        onHover: () => {},
        onClick: () => {},
      },
    };
  });

  // Add an invisible spacer node below the graph to push real nodes toward the top
  // when fitView centers the bounding box
  rfNodes.push({
    id: '__spacer__',
    type: 'default',
    position: { x: 0, y: maxY + 300 },
    data: {},
    style: { opacity: 0, width: 1, height: 1, pointerEvents: 'none' },
    selectable: false,
    draggable: false,
    focusable: false,
  } as Node);

  const rfEdges: Edge[] = graph.edges.map(e => {
    const sourceNode = graph.nodes.find(n => n.id === e.source);
    const color = sourceNode ? EDGE_COLORS[sourceNode.nodeType] ?? '#666' : '#666';
    const label = e.weightBp > 0 ? `${e.weightBp / 100}%` : undefined;

    return {
      id: `e-${e.id}`,
      source: String(e.source),
      target: String(e.destination),
      animated: true,
      label,
      style: { stroke: color, strokeWidth: 2 },
      labelStyle: { fill: '#ccc', fontSize: 10 },
      labelBgStyle: { fill: '#1e1e2e', fillOpacity: 0.8 },
    };
  });

  return { nodes: rfNodes, edges: rfEdges };
}

export function GraphRenderer({ graph, stale }: GraphRendererProps) {
  const highlightedNodeId = usePviStore(s => s.highlightedNodeId);
  const setHighlightedNodeId = usePviStore(s => s.setHighlightedNodeId);
  const setHighlightedLine = usePviStore(s => s.setHighlightedLine);

  const simMode = useSimulationStore(s => s.mode);
  const nodeStatuses = useSimulationStore(s => s.nodeStatuses);
  const activeEdgeIds = useSimulationStore(s => s.activeEdgeIds);

  const advMode = useAdversarialStore(s => s.mode);
  const advFailedNodeIds = useAdversarialStore(s => s.advFailedNodeIds);
  const advWarningNodeIds = useAdversarialStore(s => s.advWarningNodeIds);
  const advPassedNodeIds = useAdversarialStore(s => s.advPassedNodeIds);

  const diagnostics = useDiagnosticStore(s => s.result.diagnostics);

  // Compute per-node diagnostic status from diagnostic store
  const diagNodeStatuses = useMemo(() => {
    const map = new Map<number, 'error' | 'warning'>();
    for (const d of diagnostics) {
      const nid = (d.metadata as { nodeId?: number } | undefined)?.nodeId;
      if (nid == null) continue;
      if (d.severity === 'Error') {
        map.set(nid, 'error');
      } else if (d.severity === 'Warning' && map.get(nid) !== 'error') {
        map.set(nid, 'warning');
      }
    }
    return map;
  }, [diagnostics]);

  const layout = useMemo(() => layoutGraph(graph), [graph]);

  const [nodes, setNodes, onNodesChange] = useNodesState(layout.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layout.edges);

  // Sync ReactFlow state when layout changes (graph updated from source edits)
  useEffect(() => {
    setNodes(layout.nodes);
    setEdges(layout.edges);
  }, [layout, setNodes, setEdges]);

  // Update highlight state on data nodes
  const nodesWithHighlight = useMemo(() =>
    nodes.map(n => {
      // Skip the invisible spacer node
      if (n.id === '__spacer__') return n;
      const d = n.data as PviNodeData;
      const simStatus: SimNodeStatus | undefined = simMode === 'simulation'
        ? nodeStatuses[d.pviNode.id] ?? 'idle'
        : undefined;
      const advStatus: 'failed' | 'warning' | 'passed' | undefined = advMode === 'adversarial'
        ? advFailedNodeIds.has(d.pviNode.id) ? 'failed'
          : advWarningNodeIds.has(d.pviNode.id) ? 'warning'
          : advPassedNodeIds.has(d.pviNode.id) ? 'passed'
          : undefined
        : undefined;
      return {
        ...n,
        data: {
          ...n.data,
          highlighted: d.pviNode.id === highlightedNodeId,
          simStatus,
          advStatus,
          diagStatus: diagNodeStatuses.get(d.pviNode.id),
          onHover: (id: number | null) => {
            setHighlightedNodeId(id);
            if (id !== null) {
              const pn = graph.nodes.find(gn => gn.id === id);
              setHighlightedLine(pn?.span?.startLine ?? null);
            } else {
              setHighlightedLine(null);
            }
          },
          onClick: (id: number) => {
            const pn = graph.nodes.find(gn => gn.id === id);
            if (pn?.span) {
              setHighlightedLine(pn.span.startLine);
              setHighlightedNodeId(id);
            }
          },
        },
      };
    }),
    [nodes, highlightedNodeId, graph.nodes, setHighlightedNodeId, setHighlightedLine, simMode, nodeStatuses, advMode, advFailedNodeIds, advWarningNodeIds, advPassedNodeIds, diagNodeStatuses],
  );

  // Animate edges during simulation
  const edgesWithSim = useMemo(() => {
    if (simMode !== 'simulation' || activeEdgeIds.length === 0) return edges;
    const activeSet = new Set(activeEdgeIds.map(id => `e-${id}`));
    return edges.map(e => {
      if (activeSet.has(e.id)) {
        return {
          ...e,
          animated: true,
          style: { ...e.style, strokeWidth: 3, opacity: 1 },
        };
      }
      return {
        ...e,
        animated: false,
        style: { ...e.style, opacity: 0.35 },
      };
    });
  }, [edges, simMode, activeEdgeIds]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }} tabIndex={-1}>
      {stale && (
        <div style={{
          position: 'absolute',
          top: 8,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          background: 'rgba(245, 158, 11, 0.15)',
          color: '#f59e0b',
          padding: '3px 12px',
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 600,
          pointerEvents: 'none',
        }}>
          Stale graph — source has errors
        </div>
      )}
      <ReactFlow
        nodes={nodesWithHighlight}
        edges={edgesWithSim}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.1 }}
        minZoom={0.2}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{ type: 'smoothstep' }}
        className="dark-flow"
        preventScrolling={false}
        panOnScroll={false}
        panOnDrag
        zoomOnDoubleClick={false}
        disableKeyboardA11y
        panActivationKeyCode={null}
        selectionKeyCode={null}
        nodesFocusable={false}
        edgesFocusable={false}
        elementsSelectable={false}
      >
        <Controls
          showInteractive={false}
        />
        <Background
          variant={BackgroundVariant.Dots}
          color="#333"
          gap={16}
          size={1}
        />
      </ReactFlow>
    </div>
  );
}
